import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, Dna, FileCheck2, Files, HeartPulse, Lock, ShieldCheck, Users } from "lucide-react";
import { api, fmtDate, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "./ClinicianDashboard";
import { StatusBadge } from "@/components/StatusBadge";
import { ReminderRow } from "@/components/patient/RemindersPanel";

function Header({ eyebrow, title, sub }) {
  return (<div><p className="eyebrow">{eyebrow}</p><h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">{title}</h1>{sub && <p className="text-sm text-slate-600 mt-2 max-w-2xl">{sub}</p>}</div>);
}

export function LabDashboard() {
  const [stats, setStats] = useState({});
  const [patients, setPatients] = useState([]);
  useEffect(() => { api.get("/dashboard").then((r) => setStats(r.data)); api.get("/patients").then((r) => setPatients(r.data)); }, []);
  return (
    <div className="space-y-8" data-testid="lab-intake-card">
      <Header eyebrow="Lab intake" title="Result submission" sub="Upload genomic panels or lab CSV/PDF reports for consented patients. Labs cannot view clinical cases or AI drafts." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
        <StatCard testId="stat-lab-patients" label="Consented patients" value={stats.patients_accessible} Icon={Users} />
        <StatCard testId="stat-lab-docs" label="Documents uploaded" value={stats.documents} Icon={Files} tone="slate" />
        <StatCard testId="stat-lab-markers" label="Markers entered" value={stats.markers_entered} Icon={Dna} tone="blue" />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {patients.map((p) => (
          <Link key={p.id} to={`/patients/${p.id}`} data-testid={`lab-patient-${p.id}`} className="clinical-card p-6 sm:p-7 flex items-center gap-5">
            <span className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-800 grid place-items-center font-heading font-bold text-xl shrink-0">{p.name?.[0]}</span>
            <div className="flex-1"><p className="font-heading font-bold text-lg text-slate-900">{p.name}</p><p className="text-sm text-slate-500 font-mono mt-0.5">ABHA {p.abha_id}</p></div>
            <span className="text-sm text-teal-700 font-semibold">Upload results <ArrowRight className="inline w-4 h-4" /></span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function PatientDashboard() {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [cases, setCases] = useState([]);
  const [reminders, setReminders] = useState([]);
  const loadRem = () => api.get("/reminders").then((r) => setReminders(r.data)).catch(() => {});
  useEffect(() => { api.get("/patients").then((r) => setPatients(r.data)); api.get("/cases").then((r) => setCases(r.data)); loadRem(); }, []);
  const me = patients[0];
  const released = cases.filter((c) => c.status === "released");
  const inReview = cases.filter((c) => c.status !== "released");
  const upcoming = reminders.filter((r) => ["scheduled", "sent"].includes(r.status));
  return (
    <div className="space-y-8" data-testid="patient-dashboard-view">
      <Header eyebrow="My health" title={`Hello, ${user.name?.split(" ")[0]}`} sub="Your care team reviews every report before it reaches you. Here you can manage what you share and read your approved reports in plain language." />
      {!me && <div className="clinical-card p-8 text-center text-sm text-slate-500" data-testid="no-record-notice">No patient record is linked to {user.email} yet. Ask your clinic to add your email to your record.</div>}
      {me && (
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          <Link to={`/patients/${me.id}`} data-testid="patient-consent-shortcut" className="clinical-card p-6 sm:p-7 bg-gradient-to-br from-teal-900 via-teal-800 to-slate-900 text-white border-0 hover:opacity-95 min-h-[120px]">
            <ShieldCheck className="w-7 h-7 text-teal-200" /><p className="font-heading font-bold text-xl mt-3">Manage consent</p><p className="text-sm text-teal-100 mt-1">Control who can see your genomic and health data.</p>
          </Link>
          <StatCard testId="stat-patient-released" label="Approved reports" value={released.length} Icon={FileCheck2} tone="emerald" />
          <StatCard testId="stat-patient-review" label="Being reviewed by doctor" value={inReview.length} Icon={HeartPulse} tone="blue" />
        </div>
      )}
      {upcoming.length > 0 && (
        <section className="space-y-3" data-testid="patient-upcoming-tests">
          <h2 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-teal-700" /> Tests your doctor wants repeated</h2>
          <div className="grid md:grid-cols-2 gap-3">{upcoming.map((r) => <ReminderRow key={r.id} r={r} onChange={loadRem} compact />)}</div>
        </section>
      )}
      <section className="space-y-3">
        <h2 className="font-heading text-lg font-semibold text-slate-800">Your reports</h2>
        {cases.length === 0 && <p className="text-sm text-slate-500">No reports yet.</p>}
        {cases.map((c) => (
          <div key={c.id} className="clinical-card p-6 sm:p-7 flex flex-wrap items-center gap-5" data-testid={`patient-case-${c.id}`}>
            <div className="flex-1 min-w-[220px]"><p className="font-heading font-bold text-lg text-slate-900">{c.title}</p><p className="text-sm text-slate-500 mt-1">{c.status === "released" ? `Approved by ${c.doctor_name} on ${fmtDate(c.released_at)}` : c.message}</p></div>
            <StatusBadge status={c.status} />
            {c.status === "released" && <Link to={`/cases/${c.id}`} className="text-sm font-semibold text-teal-700" data-testid={`view-report-${c.id}`}>Read report →</Link>}
          </div>
        ))}
      </section>
    </div>
  );
}

export function ScopedDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [patients, setPatients] = useState([]);
  useEffect(() => { api.get("/dashboard").then((r) => setStats(r.data)); api.get("/patients").then((r) => setPatients(r.data)); }, []);
  const isIns = user.role === "insurance";
  return (
    <div className="space-y-8" data-testid={isIns ? "insurance-gated-view" : "nutritionist-matrix"}>
      <Header eyebrow={ROLE_LABELS[user.role]} title={isIns ? "Consent-gated member view" : "Nutrition & lifestyle clients"}
        sub={isIns ? "You only see members who explicitly consented to insurance access, and only released, doctor-approved summaries without genomic detail." : "You see clients who consented to nutritionist access, with released metabolic, cardiovascular and nutrition guidance."} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6 max-w-2xl">
        <StatCard testId="stat-consented" label="Consented patients" value={stats.patients_consented} Icon={ShieldCheck} tone="emerald" />
        <StatCard testId="stat-scoped-released" label="Released reports" value={stats.released_reports} Icon={FileCheck2} />
      </div>
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3 text-sm text-amber-900" data-testid="scope-notice"><Lock className="w-4 h-4 mt-0.5 shrink-0" /> Access is scoped by patient consent under DPDP. Revoked consent removes visibility immediately.</div>
      <div className="grid md:grid-cols-2 gap-5">
        {patients.length === 0 && <p className="text-sm text-slate-500 md:col-span-2" data-testid="no-consented-patients">No patients have consented to {ROLE_LABELS[user.role].toLowerCase()} access.</p>}
        {patients.map((p) => (
          <Link key={p.id} to={`/patients/${p.id}`} data-testid={`scoped-patient-${p.id}`} className="clinical-card p-6 sm:p-7 flex items-center gap-5">
            <span className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-800 grid place-items-center font-heading font-bold text-xl shrink-0">{p.name?.[0]}</span>
            <div className="flex-1"><p className="font-heading font-bold text-lg text-slate-900">{p.name}</p><p className="text-sm text-slate-500 mt-0.5">{p.sex} · DOB {fmtDate(p.dob)}</p></div>
            <StatusBadge status={p.latest_case_status} /><ArrowRight className="w-4 h-4 text-slate-400" />
          </Link>
        ))}
      </div>
    </div>
  );
}
